import React, { useState, useEffect } from 'react';

function MobileDoctorHome({ user, onNavigate }) {
  const [stats, setStats] = useState({ patients: 0, reports: 0, summaries: 0 });
  const [recentPatients, setRecentPatients] = useState([]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return '☀️ Good Morning';
    if (h < 17) return '☀️ Good Afternoon';
    if (h < 21) return '🌆 Good Evening';
    return '🌙 Good Night';
  };

  const formatDoctorName = (name) => {
    if (!name) return 'Doctor';
    const cleanName = name.replace(/^dr\.?\s+/i, '');
    return `Dr. ${cleanName.split(' ')[0]}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Access/doctor-requests/${user.id}`);
        const data = await res.json();
        const approved = data.filter(r => r.status === 'approved');
        setStats({ patients: approved.length, reports: 0, summaries: 0 });
        setRecentPatients(approved.slice(0, 2));
      } catch { }
    };
    fetchData();
  }, [user.id]);

  return (
    <div className="px-4 pt-4 pb-6 space-y-5" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* GREETING & HERO PROFILE CARD */}
      <section className="bg-gradient-to-br from-[#0B3C95] to-[#06245c] rounded-2xl p-5 text-white shadow-md relative overflow-hidden">
        <div className="relative z-10">
          <p className="text-blue-100 text-xs font-semibold tracking-wide flex items-center gap-1.5">
            {greeting()}
          </p>
          <h1 className="text-2xl font-extrabold mt-1 tracking-tight">{formatDoctorName(user?.name)}</h1>

          <div className="flex items-center gap-2 mt-3">
            <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 text-[10px] font-black px-2.5 py-0.5 rounded-md tracking-wider uppercase flex items-center gap-1">
              ✓ VERIFIED DOCTOR
            </span>
            <span className="text-xs text-blue-100/80 font-medium">
              | {user?.specialization || 'Doctor'}
            </span>
          </div>
        </div>
      </section>

      {/* STATS OVERVIEW */}
      <section>
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: 'groups', color: 'text-[#0B3C95]', bg: 'bg-[#EAF2FF]', label: 'PATIENTS', sub: 'Total Patients', value: stats.patients },
            { icon: 'description', color: 'text-emerald-600', bg: 'bg-[#E6F7F4]', label: 'REPORTS', sub: 'Total Reports', value: stats.reports },
            { icon: 'auto_awesome', color: 'text-purple-600', bg: 'bg-[#F0EBFB]', label: 'SUMMARIES', sub: 'Total Summaries', value: stats.summaries },
          ].map(stat => (
            <div 
              key={stat.label} 
              className="bg-white rounded-2xl border border-slate-100 p-3.5 flex flex-col items-center text-center shadow-xs"
            >
              <div className={`w-10 h-10 ${stat.bg} rounded-full flex items-center justify-center mb-2`}>
                <span className={`material-symbols-outlined text-xl ${stat.color}`}>{stat.icon}</span>
              </div>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{stat.label}</p>
              <p className="text-2xl font-black text-slate-900 my-0.5">{stat.value}</p>
              <p className="text-[10px] text-slate-400 font-medium">{stat.sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* QUICK ACTIONS */}
      <section>
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-extrabold text-base text-slate-900 tracking-tight">Quick Actions</h2>
          <button onClick={() => onNavigate('patients')} className="text-xs font-bold text-[#0B3C95] flex items-center gap-0.5">
            View All <span className="material-symbols-outlined text-sm">chevron_right</span>
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          {[
            { icon: 'search', color: 'text-[#0B3C95]', bg: 'bg-[#EAF2FF]', label: 'Find Patient', desc: 'Search by ID or QR', tab: 'search' },
            { icon: 'groups', color: 'text-emerald-600', bg: 'bg-[#E6F7F4]', label: 'My Patients', desc: 'View approved patients', tab: 'patients' },
            { icon: 'auto_awesome', color: 'text-purple-600', bg: 'bg-[#F0EBFB]', label: 'AI Summaries', desc: 'Generate health reports', tab: 'patients' },
            { icon: 'description', color: 'text-amber-600', bg: 'bg-amber-50', label: 'All Reports', desc: 'Patient reports', tab: 'patients' },
          ].map(action => (
            <button
              key={action.label}
              onClick={() => onNavigate(action.tab)}
              className="bg-white rounded-2xl p-3.5 border border-slate-100 shadow-xs flex items-center gap-3 active:scale-[0.98] transition-all text-left"
            >
              <div className={`w-10 h-10 ${action.bg} rounded-xl flex items-center justify-center shrink-0`}>
                <span className={`material-symbols-outlined text-xl ${action.color}`}>{action.icon}</span>
              </div>
              <div>
                <span className="font-bold text-slate-800 text-xs block leading-snug">{action.label}</span>
                <span className="text-[10px] text-slate-400 leading-tight block mt-0.5">{action.desc}</span>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* RECENT PATIENTS */}
      <section>
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-extrabold text-base text-slate-900 tracking-tight">Recent Patients</h2>
          <button 
            onClick={() => onNavigate('patients')} 
            className="text-xs font-bold text-[#0B3C95] flex items-center gap-0.5"
          >
            View All <span className="material-symbols-outlined text-sm">chevron_right</span>
          </button>
        </div>

        <div className="space-y-3">
          {recentPatients.length === 0 ? (
            <div className="bg-white rounded-2xl p-6 text-center border border-slate-100 shadow-xs">
              <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-2 text-slate-300">
                <span className="material-symbols-outlined text-2xl">groups</span>
              </div>
              <p className="text-slate-700 text-sm font-bold">No patients yet</p>
              <p className="text-slate-400 text-xs mt-1">Search and request access to get started</p>
            </div>
          ) : (
            recentPatients.map(p => (
              <button 
                key={p.id} 
                onClick={() => onNavigate('patients', p)}
                className="w-full bg-white rounded-2xl p-3.5 flex items-center justify-between border border-slate-100 shadow-xs active:scale-[0.99] transition-all text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-[#0B3C95] text-white font-black text-base flex items-center justify-center shrink-0">
                    {(p.patientName || 'P').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-sm">{p.patientName || `Patient #${p.patientId}`}</p>
                    <p className="text-[10px] text-slate-400 font-mono mt-0.5">{p.patientUid || 'MHJ-XXXX'}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                      <span className="text-[9px] font-bold text-emerald-600 uppercase tracking-wider">Active</span>
                    </div>
                  </div>
                </div>
                <span className="material-symbols-outlined text-slate-300">chevron_right</span>
              </button>
            ))
          )}
        </div>
      </section>

    </div>
  );
}

export default MobileDoctorHome;